/**
 * 内容提取器（本地规则版）
 * ------------------------------------------------------------
 * 输入：pdfParser 产出的 { pages: [{ page, lines }] }
 * 输出：草稿包（统一结构），**永远不直接入库**，只写进 imports 集合等人工审核。
 *
 * 核心原则（用户明确要求）：
 *   1. 识别不到的字段一律留空并标记 needsReview，绝不"猜"或"补全"
 *   2. 每个字段带 confidence（0~1）与 sourceLine（原始行号/页码），便于对照核查
 *   3. LLM 只作为可选补全手段（见 llmAdapter.js），且只写草稿
 *
 * 结构约定见 data/schema/import-draft.schema.json
 */
import { nowISO, uid, hash } from '../utils/index.js';

/* ------------------------------ 正则词典 ------------------------------ */

const RE_UNIT = /(?:^|\s)(?:Unit|UNIT|unit)\s*(\d{1,2})\b(.*)$/;
const RE_UNIT_CN = /第\s*([一二三四五六七八九十\d]{1,3})\s*单元(.*)$/;
const CN_NUM = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10 };
/** 单词行：英文（可含空格/连字符）+ 音标(可选) + 词性(可选) + 中文 */
const RE_WORD = /^([A-Za-z][A-Za-z'’\-\s]{0,30}?)\s*(\/[^/]{1,40}\/)?\s*(n\.|v\.|adj\.|adv\.|pron\.|prep\.|conj\.|num\.|art\.|int\.)?\s*([\u4e00-\u9fa5][\u4e00-\u9fa5；;，,、\s（）()·]*)$/;
/** 句子行：英文句子 + 中文翻译 */
const RE_SENT = /^([A-Z][A-Za-z0-9'’\-\s,.!?…"]{5,120}[.!?…"]?)\s+([\u4e00-\u9fa5][^A-Za-z]*)$/;
/** 语法点：中文规则行（含"用/表示/后接/复数/时态"等） */
const RE_GRAMMAR = /([\u4e00-\u9fa5]{2,30}(?:用法|表示|后接|的复数|时态|句型|搭配|变化|结构)[\u4e00-\u9fa5，。、；：""''（）()]{0,60})/;
/** 题型：选择题 */
const RE_Q_CHOICE = /^(\d{1,2})[.、)]\s*(.{3,120}?)\s*[（(]\s*[）)]\s*$/;
const RE_OPT = /^([A-D])[.、)]\s*(.+)$/;

const clean = s => String(s || '').replace(/\s+/g, ' ').trim();

/* --------------------------- 单元切分 --------------------------- */

function detectUnit(lines) {
  for (const l of lines) {
    const m = l.text.match(RE_UNIT);
    if (m) return { no: Number(m[1]), title: clean(m[2]).replace(/^[:：\-\s]+/, ''), matched: l.text };
    const c = l.text.match(RE_UNIT_CN);
    if (c) {
      const raw = c[1];
      const no = CN_NUM[raw] || Number(raw) || 0;
      return { no, title: clean(c[2]).replace(/^[:：\-\s]+/, ''), matched: l.text };
    }
  }
  return null;
}

/* --------------------------- 主体提取 --------------------------- */

/**
 * 从解析结果提取草稿
 * @param {object} parsed   parseDocument() 的返回值
 * @param {object} meta     { textbookId, kind: 'textbook'|'workbook'|'paper' }
 */
export function extractDraft(parsed, meta = {}) {
  const allLines = [];
  parsed.pages.forEach(p => p.lines.forEach(l => allLines.push({ ...l, page: p.page })));

  const unitInfo = detectUnit(allLines);
  const words = [];
  const sentences = [];
  const grammar = [];
  const questions = [];
  const warnings = [];

  if (!unitInfo) {
    warnings.push('未识别到单元编号（Unit N / 第 N 单元），请在预览页手动填写。');
  }
  if (!parsed.hasText) {
    warnings.push('该文件几乎没有文字层，可能是扫描件。请改用文字版 PDF 或手工录入。');
  }

  let pendingQuestion = null;

  for (const line of allLines) {
    const t = clean(line.text);
    if (!t) continue;

    // 选项行 → 归到当前题目
    const opt = t.match(RE_OPT);
    if (opt && pendingQuestion) {
      pendingQuestion.options.push(clean(opt[2]));
      continue;
    }

    // 新题目
    const q = t.match(RE_Q_CHOICE);
    if (q && t.includes('（') || (q && t.includes('('))) {
      if (pendingQuestion) questions.push(pendingQuestion);
      pendingQuestion = {
        id: uid('q'),
        type: 'choice',
        skill: 'G',
        level: 1,
        prompt: clean(q[2]),
        options: [],
        answer: [],       // 练习册通常没有答案，留空标待确认
        explain: '',
        page: line.page,
        sourceLine: t,
        confidence: 0.5,
        needsReview: true,
        reviewNote: '未识别到答案，请手工填写或从答案页补充',
      };
      if (t.match(/[（(]\s*([A-D])\s*[）)]/)) {
        const inline = t.match(/[（(]\s*([A-D])\s*[）)]/);
        pendingQuestion.answer = [inline[1]];
        pendingQuestion.needsReview = false;
        pendingQuestion.reviewNote = '';
        pendingQuestion.confidence = 0.7;
      }
      continue;
    }

    // 单词
    const w = t.match(RE_WORD);
    if (w && w[4]) {
      const en = clean(w[1]);
      // 排除「句子被误判成单词」的情况：含句末标点或词数 > 3 则跳过
      const wordCount = en.split(/\s+/).length;
      if (wordCount <= 3 && !/[.!?]$/.test(en)) {
        const zh = clean(w[4]);
        words.push({
          id: uid('w'),
          en,
          phonetic: w[2] || '',
          pos: w[3] || '',
          zh,
          phrase: '',
          phraseZh: '',
          sentence: '',
          sentenceZh: '',
          page: line.page,
          sourceLine: t,
          confidence: w[2] ? 0.9 : 0.7,
          needsReview: !w[2],
          reviewNote: w[2] ? '' : '未识别到音标，可稍后补充',
        });
        continue;
      }
    }

    // 句子
    const s = t.match(RE_SENT);
    if (s) {
      const en = clean(s[1]);
      if (en.split(/\s+/).length >= 3) {
        sentences.push({
          id: uid('s'),
          en,
          zh: clean(s[2]),
          pattern: '',
          keyPattern: false,
          page: line.page,
          sourceLine: t,
          confidence: 0.8,
          needsReview: false,
          reviewNote: '',
        });
        continue;
      }
    }

    // 语法点
    const g = t.match(RE_GRAMMAR);
    if (g && t.length < 90) {
      grammar.push({
        id: uid('g'),
        category: '',         // 分类必须人工确认，不自动编造
        rule: clean(g[1]),
        examples: [],
        page: line.page,
        sourceLine: t,
        confidence: 0.5,
        needsReview: true,
        reviewNote: '语法分类需要人工确认',
      });
    }
  }
  if (pendingQuestion) questions.push(pendingQuestion);

  const draft = {
    id: unitInfo ? hash(`${meta.textbookId || ''}|u${unitInfo.no}`) : uid('draft'),
    textbookId: meta.textbookId || '',
    kind: meta.kind || 'textbook',
    sourceFile: parsed.name,
    pageCount: parsed.pageCount,
    unit: {
      no: unitInfo?.no || 0,
      title: unitInfo?.title || '',
      theme: '',
      objectives: [],
      needsReview: !unitInfo,
    },
    words,
    sentences,
    grammar,
    questions,
    warnings,
    status: 'pending_review',      // pending_review | committed | discarded
    stats: {
      words: words.length,
      sentences: sentences.length,
      grammar: grammar.length,
      questions: questions.length,
      needsReview:
        words.filter(x => x.needsReview).length +
        sentences.filter(x => x.needsReview).length +
        grammar.filter(x => x.needsReview).length +
        questions.filter(x => x.needsReview).length +
        (unitInfo ? 0 : 1),
    },
    createdAt: nowISO(),
  };

  return draft;
}

/** 合并多个文件的草稿（同一单元的多份 PDF 一起导入） */
export function mergeDrafts(drafts) {
  if (drafts.length === 1) return drafts[0];
  const first = drafts[0];
  const merged = {
    ...first,
    id: uid('draft'),
    sourceFile: drafts.map(d => d.sourceFile).join(' + '),
    pageCount: drafts.reduce((n, d) => n + d.pageCount, 0),
    words: drafts.flatMap(d => d.words),
    sentences: drafts.flatMap(d => d.sentences),
    grammar: drafts.flatMap(d => d.grammar),
    questions: drafts.flatMap(d => d.questions),
    warnings: [...new Set(drafts.flatMap(d => d.warnings))],
  };
  merged.stats = {
    words: merged.words.length,
    sentences: merged.sentences.length,
    grammar: merged.grammar.length,
    questions: merged.questions.length,
    needsReview: [...merged.words, ...merged.sentences, ...merged.grammar, ...merged.questions]
      .filter(x => x.needsReview).length + (merged.unit.needsReview ? 1 : 0),
  };
  return merged;
}
